import { ADMIN_CONFIG_VALUE_TYPES, type AdminConfigValueType } from '@carcheck/shared';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { FilterBar } from '../../../components/filters/FilterBar';
import { SearchInput } from '../../../components/filters/SearchInput';
import { groupLabel } from '../_lib/configDisplay';
import type { ConfigFilters } from '../_lib/configRows';

/**
 * Filter toolbar for the runtime-config table (AUT-131) — key search +
 * value-type select + domain-group select + clear. All CLIENT-side over
 * the bounded ~44-key set (architect guardrail #7 — no server params).
 *
 * Search uses the shared debounced `<SearchInput>` with `minLength={0}`
 * (the set is tiny; no min-length table-scan guard needed). The type +
 * group selects are single-value `<TextField select>`s with an "all"
 * sentinel.
 */
export interface ConfigFilterBarProps {
  filters: ConfigFilters;
  onSearchChange: (search: string) => void;
  onTypeChange: (type: AdminConfigValueType | null) => void;
  onGroupChange: (group: string | null) => void;
  /** Distinct domain groups present in the list (for the group dropdown). */
  groups: string[];
  canClear: boolean;
  onClear: () => void;
}

const ALL = '__all__';

export const ConfigFilterBar = ({
  filters,
  onSearchChange,
  onTypeChange,
  onGroupChange,
  groups,
  canClear,
  onClear,
}: ConfigFilterBarProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <FilterBar canClear={canClear} onClear={onClear}>
      <SearchInput
        minLength={0}
        initialValue={filters.search}
        placeholder={t('config.filters.searchPlaceholder')}
        onSearch={onSearchChange}
      />

      <TextField
        select
        size="small"
        label={t('config.filters.typeLabel')}
        value={filters.type ?? ALL}
        onChange={(e) =>
          onTypeChange(e.target.value === ALL ? null : (e.target.value as AdminConfigValueType))
        }
        sx={{ minWidth: 160 }}
      >
        <MenuItem value={ALL}>{t('config.filters.allTypes')}</MenuItem>
        {ADMIN_CONFIG_VALUE_TYPES.map((type) => (
          <MenuItem key={type} value={type}>
            {t(`config.type.${type}`)}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        size="small"
        label={t('config.filters.groupLabel')}
        value={filters.group ?? ALL}
        onChange={(e) => onGroupChange(e.target.value === ALL ? null : e.target.value)}
        sx={{ minWidth: 180 }}
      >
        <MenuItem value={ALL}>{t('config.filters.allGroups')}</MenuItem>
        {groups.map((group) => (
          // AUT-284 — the dropdown reads «Цены», not `pricing`; the raw
          // prefix stays the VALUE (it is what `groupOf` matches on).
          <MenuItem key={group} value={group}>
            {groupLabel(group, t)}
          </MenuItem>
        ))}
      </TextField>
    </FilterBar>
  );
};
